import { fetchMailEmlBatch, fetchMailStoredUids } from "@/lib/api/storage";
import {
  MAIL_STORAGE_FETCH_BATCH,
  MAIL_STORAGE_FETCH_GAP_MS,
  MAIL_STORAGE_FOLDER_MAX,
} from "@/lib/constants/mailStorage";
import { setFolderStorageProgress } from "@/lib/storage/folderStorageProgress";
import { storeFetchedEmlItems } from "@/lib/storage/storeFetchedEmlItems";
import { notifyMailStorageStatsRefresh } from "@/features/accounts/MailStorageQuotaSection";

type FolderKey = string;

const syncInflight = new Map<FolderKey, Promise<number>>();

function folderKey(accountId: number, folder: string): FolderKey {
  return `${accountId}\0${folder}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export type FolderManualStorageOptions = {
  count: number;
  includeAttachments: boolean;
  uids: number[];
  onProgress?: (done: number, total: number) => void;
};

/**
 * Téléchargement manuel d’un dossier — archive .eml sur DD02 serveur.
 */
export async function downloadFolderToServerStorage(
  accountId: number,
  folder: string,
  options: FolderManualStorageOptions
): Promise<number> {
  const key = folderKey(accountId, folder);
  const existing = syncInflight.get(key);
  if (existing) return existing;

  const limit = Math.min(
    MAIL_STORAGE_FOLDER_MAX,
    Math.max(1, Math.floor(options.count))
  );

  setFolderStorageProgress(accountId, folder, {
    active: true,
    progress: 0,
    label: `Préparation de ${folder}…`,
    error: null,
    done: 0,
    total: limit,
  });

  const run = async (): Promise<number> => {
    const allUids = [...new Set(options.uids.filter((uid) => uid > 0))];
    if (allUids.length === 0) {
      setFolderStorageProgress(accountId, folder, { active: false, label: "" });
      return 0;
    }

    const storedRes = await fetchMailStoredUids(accountId, folder);
    const storedSet = new Set(
      storedRes.ok && storedRes.data.success ? storedRes.data.uids : []
    );
    const toFetch = allUids.filter((uid) => !storedSet.has(uid)).slice(0, limit);
    if (toFetch.length === 0) {
      setFolderStorageProgress(accountId, folder, {
        active: false,
        progress: 100,
        label: "Déjà copiés sur le serveur",
        done: 0,
        total: 0,
      });
      return 0;
    }

    let cancelled = false;
    const abort = () => {
      cancelled = true;
    };

    setFolderStorageProgress(accountId, folder, {
      active: true,
      progress: 0,
      label: `Copie de ${folder}…`,
      error: null,
      done: 0,
      total: toFetch.length,
      abort,
    });

    let totalStored = 0;
    let done = 0;

    for (let i = 0; i < toFetch.length; i += MAIL_STORAGE_FETCH_BATCH) {
      if (cancelled) break;
      const batch = toFetch.slice(i, i + MAIL_STORAGE_FETCH_BATCH);
      const res = await fetchMailEmlBatch({
          accountId,
          folder,
          uids: batch,
          includeAttachments: options.includeAttachments,
          syncProgress: {
            active: true,
            done,
            total: toFetch.length,
            label: `Copie de ${folder}… ${done}/${toFetch.length}`,
          },
        });
      if (!res.ok) {
        setFolderStorageProgress(accountId, folder, {
          active: false,
          error: res.message,
          label: "",
        });
        break;
      }
      totalStored += await storeFetchedEmlItems(accountId, folder, res.data.items);
      done += batch.length;
      const pct = Math.round((done / toFetch.length) * 100);
      options.onProgress?.(done, toFetch.length);
      setFolderStorageProgress(accountId, folder, {
        progress: pct,
        done,
        total: toFetch.length,
        label: `Copie de ${folder}… ${done}/${toFetch.length}`,
      });
      notifyMailStorageStatsRefresh();
      if (i + MAIL_STORAGE_FETCH_BATCH < toFetch.length) {
        await delay(MAIL_STORAGE_FETCH_GAP_MS);
      }
    }

    setFolderStorageProgress(accountId, folder, {
      active: false,
      progress: 100,
      label:
        totalStored > 0
          ? `${totalStored} e-mail${totalStored > 1 ? "s" : ""} copié${totalStored > 1 ? "s" : ""}`
          : cancelled
            ? "Copie annulée"
            : "Terminé",
      done: toFetch.length,
      total: toFetch.length,
    });
    notifyMailStorageStatsRefresh();

    return totalStored;
  };

  const promise = run().finally(() => {
    syncInflight.delete(key);
  });
  syncInflight.set(key, promise);
  return promise;
}
